import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import './CheckoutSuccess.css';

interface SubscriptionData {
  id: string;
  status: string;
  planId: string;
  plan?: {
    name: string;
    displayName: string;
  };
  billingCycle?: string;
  currentPeriodEnd?: string;
}

export const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirming, setConfirming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);

  const confirmSubscription = useCallback(async () => {
    try {
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        setError('No session ID found');
        setConfirming(false);
        return;
      }

      const response = await axiosInstance.post(
        '/subscription/confirm',
        { sessionId }
      );

      if (response.data.success) {
        setSubscription(response.data.data);
        setConfirming(false);
      } else {
        setError(response.data.message || 'Failed to confirm subscription');
        setConfirming(false);
      }
    } catch (err) {
      console.error('Error confirming subscription:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to confirm subscription'
      );
      setConfirming(false);
    }
  }, [searchParams]);

  useEffect(() => {
    confirmSubscription();
  }, [confirmSubscription]);

  if (confirming) {
    return (
      <div className="checkout-success">
        <div className="success-container loading">
          <div className="spinner"></div>
          <h2>Processing your subscription...</h2>
          <p>Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-success">
        <div className="success-container error">
          <div className="error-icon">⚠</div>
          <h2>Subscription Confirmation Failed</h2>
          <p>{error}</p>
          <button
            className="btn-primary"
            onClick={() => navigate('/settings/billing')}
          >
            Return to Billing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success">
      <div className="success-container success">
        <div className="success-icon">✓</div>
        <h2>Subscription Successful!</h2>
        <p>Thank you for upgrading your plan.</p>

        <div className="confirmation-details">
          <div className="detail-row">
            <label>Plan:</label>
            <span>{subscription?.plan?.displayName}</span>
          </div>
          <div className="detail-row">
            <label>Billing Period:</label>
            <span>
              {subscription?.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
            </span>
          </div>
          <div className="detail-row">
            <label>Status:</label>
            <span className="status-active">
              {subscription?.status === 'active' ? 'Active' : subscription?.status}
            </span>
          </div>
          <div className="detail-row">
            <label>Next Renewal:</label>
            <span>
              {subscription?.currentPeriodEnd
                ? new Date(subscription.currentPeriodEnd).toLocaleDateString(
                    'en-US',
                    {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }
                  )
                : 'N/A'}
            </span>
          </div>
        </div>

        <div className="next-steps">
          <h3>What's Next?</h3>
          <ul>
            <li>Your new plan features are now active</li>
            <li>A confirmation email has been sent to your inbox</li>
            <li>You can manage your subscription anytime in your billing settings</li>
          </ul>
        </div>

        <div className="action-buttons">
          <button
            className="btn-primary"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
          <button
            className="btn-secondary"
            onClick={() => navigate('/settings/billing')}
          >
            View Billing Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
